//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  CampaignType,
  ContentLibraryFilter,
  ContentLibraryV2RequestBody,
  ModerationStatus,
  NetworkType,
  RIGHTS_STATUS,
} from '@apis/content-library/contentLibraryApiModel';

import { RoutableStateName } from 'app/top-level-states';

export const GRID_CONTENT_DISTRIBUTION_CARD_HEIGHT = 364;

/**
 * Pages where the bulk reject action should be available.
 */
export const AI_MODERATION_BULK_REJECT_PAGES: ReadonlySet<RoutableStateName> =
  new Set<RoutableStateName>(['library.distribute', 'library.needs-action']);

const NOT_DISTRIBUTED_FILTER: ContentLibraryFilter = {
  '@type': 'distribution',
  distributions: [{ '@type': 'galleries' }],
  negated: true,
};

// Content that has been uploaded through media import, or is part of campaigns
const getNonOrganicallySourcedContent = (
  isCreatorCampaignsEnabled: boolean,
): ContentLibraryFilter => {
  return {
    '@type': 'or',
    filters: [
      {
        '@type': 'network',
        networkTypes: [NetworkType.MEDIA_IMPORT],
      },
      {
        '@type': 'campaignType',
        types: isCreatorCampaignsEnabled
          ? [CampaignType.Sampling, CampaignType.Creator]
          : [CampaignType.Sampling],
      },
    ],
  };
};

// Content that has been approved by moderation but is not yet distributed to galleries,
// and has rights and products tagged
export const getReadyToDistributeInitialFilterParams = (
  isSourcedContentEnabled: boolean,
  isCreatorCampaignsEnabled: boolean,
): ContentLibraryV2RequestBody => {
  return {
    shouldHydrateModerationMetadata: true,
    query: {
      '@type': 'and',
      filters: [
        {
          '@type': 'moderationStatus',
          status: [ModerationStatus.Approved],
        },
        { ...NOT_DISTRIBUTED_FILTER },
        {
          '@type': 'rightsStatus',
          status: [RIGHTS_STATUS.OWNED, RIGHTS_STATUS.APPROVED],
        },
        {
          '@type': 'hasProducts',
          hasProducts: true,
        },
        ...(!isSourcedContentEnabled
          ? [getNonOrganicallySourcedContent(isCreatorCampaignsEnabled)]
          : []),
      ],
    },
  };
};

// Content that has been approved by moderation, is not yet distributed to galleries,
// does not have rejected rights, and is either missing rights or product tagging
export const needsActionInitialFilterParams: ContentLibraryV2RequestBody = {
  shouldHydrateModerationMetadata: true,
  query: {
    '@type': 'and',
    filters: [
      {
        '@type': 'moderationStatus',
        status: [ModerationStatus.Approved],
      },
      { ...NOT_DISTRIBUTED_FILTER },
      {
        '@type': 'or',
        filters: [
          {
            '@type': 'rightsStatus',
            negated: true,
            status: [RIGHTS_STATUS.APPROVED, RIGHTS_STATUS.OWNED],
          },
          {
            '@type': 'hasProducts',
            hasProducts: false,
          },
        ],
      },
      {
        '@type': 'rightsStatus',
        negated: true,
        status: [RIGHTS_STATUS.EXPIRED],
      },
    ],
  },
};

// Content that (has been rejected by moderation or (has been approved by moderation but has rejected rights)),
// and hasn't been distributed to galleries
export const getRejectedInitialFilterParams = (
  isSourcedContentEnabled: boolean,
  isCreatorCampaignsEnabled: boolean,
): ContentLibraryV2RequestBody => {
  return {
    shouldHydrateModerationMetadata: true,
    query: {
      '@type': 'and',
      filters: [
        { ...NOT_DISTRIBUTED_FILTER },
        {
          '@type': 'or',
          filters: [
            {
              '@type': 'moderationStatus',
              status: [ModerationStatus.Rejected],
            },
            {
              '@type': 'and',
              filters: [
                {
                  '@type': 'moderationStatus',
                  status: [ModerationStatus.Approved],
                },
                {
                  '@type': 'rightsStatus',
                  status: [RIGHTS_STATUS.EXPIRED],
                },
              ],
            },
          ],
        },
        ...(!isSourcedContentEnabled
          ? [getNonOrganicallySourcedContent(isCreatorCampaignsEnabled)]
          : []),
      ],
    },
  };
};
